/*
 * API client for the Softaware Greek Editor Word Add-in.
 *
 * Auth endpoints use `credentials: "include"` so the HttpOnly `sw_session`
 * cookie is handled automatically by the WebView engine — no manual token
 * passing between steps is required.
 *
 * Account-owned token selection mirrors the web app: the chosen token is
 * stored on the server-side session and books endpoints resolve it from the
 * same cookie-backed account session. A raw `x-service-token` header is only
 * attached when a legacy caller explicitly passes one.
 *
 * Ported from api.js — fetch logic is unchanged; only types were added.
 *
 * Layered rate limiting (Tasks 11+): every non-OK response surfaces as a typed
 * `ApiRequestError` carrying `status`, `code`, `retryAfterSeconds`, `policy`,
 * and `taskId`. The `Retry-After` header takes precedence over the body
 * `retryAfterSeconds` field so HTTP-level cooldowns always win. Auth and scan
 * functions dedupe simultaneous in-flight calls by a normalized key, so a
 * double-click on "Send code" or a rapid re-scan fires one request.
 */

import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import type {
  CatalogPreferences,
  CatalogSections,
  DocComment,
  DocSuggestion,
  DocReviewFinding,
  DocxReviewMode,
  KarlAssists,
  KarlCapability,
  KarlDecisionInput,
  KarlVerdict,
  KarlVerdictBatchEstimate,
  KarlVerdictBatchResult,
  KarlVerdictInput,
  KarlVerdictResult,
  LiveSuggestion,
  Occurrence,
  PreferenceValues,
  RecentRuleSet,
  Repetition,
  Rule,
  RuleDependencies,
  SessionUser,
  SubscriptionOverview,
} from "../state/types";

/*
 * All fetch calls use relative paths (/api/...) so they go to the same
 * origin as the add-in dev server (https://localhost:3300). Webpack's
 * devServer proxy then forwards them to http://localhost:3000 on the backend.
 * This avoids mixed-content blocking and cross-origin cookie restrictions.
 */
const BOOKS_BASE = "/api/books";
const AUTH_BASE = "/api/auth";
const SUBSCRIPTIONS_BASE = "/api/subscriptions";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const CLIENT_TYPE_HEADERS = { "x-client": "word_addin" } as const;

interface ApiEnvelope<T> {
  data?: T | null;
  error?: {
    code?: string;
    message?: string;
    details?: Array<{ field?: string; issue?: string }>;
  };
  message?: string;
  meta?: { requestId?: string };
}

/**
 * Typed failure surfaced by every API helper. Mirrors the softaware-apis
 * rate-limit contract: `retryAfterSeconds` comes from the `Retry-After` header
 * when present (and wins over any body field), `policy` is read from
 * `error.details[].field === "policy"`, and `taskId` is read from structured
 * error details. Plain non-OK responses also throw this
 * class so callers can branch on `status` / `code` without string matching.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterSeconds: number | null;
  readonly policy: string | null;
  readonly taskId: string | null;
  /* Raw `error.details`, so callers can name the Karl window that ran out. */
  readonly details: Array<{ field?: string; issue?: string }>;

  constructor(options: {
    message: string;
    status: number;
    code: string;
    retryAfterSeconds?: number | null;
    policy?: string | null;
    taskId?: string | null;
    details?: Array<{ field?: string; issue?: string }>;
  }) {
    super(options.message);
    this.name = "ApiRequestError";
    this.status = options.status;
    this.code = options.code;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
    this.policy = options.policy ?? null;
    this.taskId = options.taskId ?? null;
    this.details = options.details ?? [];
  }
}

export function isQuotaError(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  if (error.status === 429 && error.code === "SERVICE_QUOTA_EXCEEDED") return true;
  if (error.status === 403 && error.code === "SERVICE_FORBIDDEN") return true;
  return false;
}

export interface SessionData {
  user?: SessionUser;
  [key: string]: unknown;
}

export interface LoginRequestResult {
  mode?: string;
  delivery?: unknown;
  [key: string]: unknown;
}

export interface PasskeyItem {
  passkeyId: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
  lastUsedAt?: string | null;
  transports?: string[];
  deviceType?: string;
  backedUp?: boolean;
}

export interface PasskeyAuthOptionsResult {
  challengeId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

export interface PasskeyRegistrationOptionsResult {
  challengeId: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}

export interface DeletePasskeyResult {
  passkeyId: string;
  deleted: boolean;
}

export type AccountDevPersona = "author" | "editor" | "publisher" | "reviewer";

export interface RuleCatalog {
  rules: Rule[];
  preferences: CatalogPreferences;
  dependencies: RuleDependencies;
  sections: CatalogSections;
  /* Missing on older API builds; treat that as Karl switched off. */
  karl: KarlCapability | null;
}

export interface ApplyTextResult {
  correctedText?: string;
  summary?: unknown;
  changes?: DocSuggestion[];
  comments?: DocComment[];
  report?: { changes?: DocSuggestion[]; comments?: DocComment[]; [key: string]: unknown } | null;
  [key: string]: unknown;
}

export interface ScanSuggestionsResult {
  changes?: DocReviewFinding[];
  comments?: DocComment[];
  summary?: unknown;
  wordCount?: number;
  [key: string]: unknown;
}

export interface ScanTextSuggestionsResult {
  changes?: LiveSuggestion[];
  comments?: DocComment[];
  summary?: unknown;
  wordCount?: number;
  [key: string]: unknown;
}

export interface ScanRepetitionsResult {
  repetitions?: Repetition[];
  statistics?: unknown;
  [key: string]: unknown;
}

export interface RepetitionOptions {
  windowSize?: number;
  minOccurrences?: number;
  customStopwords?: string[];
}

export interface VerifyOtpOptions {
  markTrusted?: boolean;
  trustedDeviceLabel?: string;
}

/* ─── Utility helpers ────────────────────────────────────────────────────── */

/** Convert base64 string to a Blob (for multipart DOCX uploads). */
export function base64ToBlob(base64: string, mimeType: string = DOCX_MIME): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array<number>(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

/** Convert an ArrayBuffer to a base64 string. */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Parse a JSON error response into a typed `ApiRequestError`. The
 * `Retry-After` header takes precedence over the body `retryAfterSeconds`
 * field; if neither is present, `retryAfterSeconds` is `null`. Exposed for
 * tests; production code should let `requireOk` throw.
 */
export async function parseApiError(response: Response): Promise<ApiRequestError> {
  let code = "";
  let message = `Request failed with status ${response.status}`;
  let policy: string | null = null;
  let retryAfterSeconds: number | null = null;
  let taskId: string | null = null;
  const details: Array<{ field?: string; issue?: string }> = [];

  const headerRetry = response.headers?.get?.("Retry-After");
  if (headerRetry) {
    const parsed = Number(headerRetry);
    if (Number.isFinite(parsed) && parsed >= 0) {
      retryAfterSeconds = parsed;
    } else {
      const retryAt = Date.parse(headerRetry);
      if (Number.isFinite(retryAt)) {
        retryAfterSeconds = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
      }
    }
  }

  try {
    const body = (await response.json()) as ApiEnvelope<unknown>;
    if (body?.error) {
      if (typeof body.error.code === "string" && body.error.code) code = body.error.code;
      if (typeof body.error.message === "string" && body.error.message)
        message = body.error.message;
      if (Array.isArray(body.error.details)) {
        for (const detail of body.error.details) {
          if (!detail || typeof detail !== "object") continue;
          details.push({
            ...(typeof detail.field === "string" ? { field: detail.field } : {}),
            ...(typeof detail.issue === "string" ? { issue: detail.issue } : {}),
          });
          if (detail.field === "policy" && typeof detail.issue === "string") {
            policy = detail.issue;
          } else if (detail.field === "retryAfterSeconds" && retryAfterSeconds === null) {
            const parsed = Number(detail.issue);
            if (Number.isFinite(parsed) && parsed >= 0) retryAfterSeconds = parsed;
          } else if (
            (detail.field === "taskId" || detail.field === "task_id") &&
            typeof detail.issue === "string"
          ) {
            taskId = detail.issue;
          }
        }
      }
    } else if (typeof body?.message === "string" && body.message) {
      message = body.message;
    }
  } catch {
    // Body wasn't JSON — fall back to the status text we already have.
  }

  const withCode = code ? `${code}: ${message}` : message;
  const withTask = taskId ? `${withCode} [task: ${taskId}]` : withCode;

  return new ApiRequestError({
    message: withTask,
    status: response.status,
    code: code || "HTTP_ERROR",
    retryAfterSeconds,
    policy,
    taskId,
    details,
  });
}

async function requireOk(response: Response): Promise<Response> {
  if (!response.ok) {
    throw await parseApiError(response);
  }
  return response;
}

/**
 * Dedupe simultaneous calls by key. When two callers fire the same operation
 * before the first one settles, both await the same in-flight promise — so a
 * rapid double-click on "Send code" or a re-triggered scan only hits the
 * network once. The slot is released once the promise settles, so a later
 * (deliberate) call will re-fetch as expected.
 */
const inflight = new Map<string, Promise<unknown>>();

export function coalesceInFlight<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = fn().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}

function normalizeEmail(email: string): string {
  return String(email || "")
    .trim()
    .toLowerCase();
}

function stableKey(parts: Record<string, unknown>): string {
  // Deep-stable key/value serializer. `JSON.stringify(value, replacerArray)` only
  // filters at the top level and loses nested values whose keys are not in the
  // array, so we walk the tree ourselves and sort object keys at every level.
  const seen = new WeakSet<object>();
  const walk = (value: unknown): unknown => {
    if (value === null || typeof value !== "object") return value;
    if (seen.has(value as object)) return null;
    seen.add(value as object);
    if (Array.isArray(value)) return value.map(walk);
    const obj = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) sorted[key] = walk(obj[key]);
    return sorted;
  };
  return JSON.stringify(walk(parts));
}

const buildServiceHeaders = (token?: string): Record<string, string> => {
  const normalized = String(token || "").trim();
  return normalized ? { "x-service-token": normalized } : {};
};

async function readData<T>(response: Response): Promise<T | null> {
  const result = (await response.json()) as ApiEnvelope<T>;
  return result?.data ?? null;
}

/* ─── Auth API ───────────────────────────────────────────────────────────── */

/**
 * Check whether an active session cookie already exists.
 * Returns the session+user data when logged in, or null when not.
 */
export async function fetchSession(): Promise<SessionData | null> {
  const response = await fetch(`${AUTH_BASE}/session`, {
    method: "GET",
    headers: { Accept: "application/json", ...CLIENT_TYPE_HEADERS },
    credentials: "include",
  });

  if (response.status === 401) return null;
  return readData<SessionData>(response);
}

/**
 * Step 1 of login: send an OTP to the user's email address.
 * Returns `{ mode, delivery }` — mode is "otp" normally, or "trusted_device"
 * when the device cookie lets the backend skip OTP entirely.
 *
 * Simultaneous triggers for the same (normalized) email coalesce into one
 * network call so a double-click on "Send code" never sends two OTPs.
 */
export async function requestLoginOtp(
  email: string,
  password: string
): Promise<LoginRequestResult | null> {
  const key = `requestLoginOtp:${normalizeEmail(email)}`;
  return coalesceInFlight(key, async () => {
    const response = await fetch(`${AUTH_BASE}/login/request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...CLIENT_TYPE_HEADERS,
      },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    await requireOk(response);
    return readData<LoginRequestResult>(response);
  });
}

/**
 * Step 2 of login: verify the OTP code and establish a session cookie.
 * Pass `markTrusted: true` and an optional `trustedDeviceLabel` to register
 * the current machine as a trusted device (skips OTP next time).
 *
 * Simultaneous verifications for the same email + code coalesce into one
 * network call; different codes run independently.
 */
export async function verifyLoginOtp(
  email: string,
  code: string,
  { markTrusted = false, trustedDeviceLabel = "" }: VerifyOtpOptions = {}
): Promise<SessionData | null> {
  const key = `verifyLoginOtp:${normalizeEmail(email)}:${String(code).trim()}`;
  return coalesceInFlight(key, async () => {
    const response = await fetch(`${AUTH_BASE}/login/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...CLIENT_TYPE_HEADERS,
      },
      credentials: "include",
      body: JSON.stringify({ email, code, markTrusted, trustedDeviceLabel }),
    });

    await requireOk(response);
    return readData<SessionData>(response);
  });
}

/*
 * Local test sessions are limited to named personas; backend environment and
 * production guards remain authoritative even if this helper is called.
 */
export async function loginDevelopmentPersona(
  persona: AccountDevPersona
): Promise<SessionData | null> {
  const response = await fetch(`${AUTH_BASE}/dev-login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({ persona }),
  });

  await requireOk(response);
  return readData<SessionData>(response);
}

/** Clear the session cookie on the backend and sign the user out. */
export async function logoutAccount(): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/logout`, {
    method: "POST",
    headers: { Accept: "application/json", ...CLIENT_TYPE_HEADERS },
    credentials: "include",
  });

  await requireOk(response);
}

/*
 * Passkey authentication and management helpers conforming to the frozen
 * cross-client contract. Endpoints use standard envelopes with relative /api/auth
 * routing and forward the x-client marker with credentials: "include".
 */

/**
 * Request WebAuthn authentication options from the backend.
 * Supports discoverable credentials (empty body) and optional email hint without account enumeration.
 * Returns { challengeId, options }.
 */
export async function fetchPasskeyAuthOptions(
  emailHint?: string
): Promise<PasskeyAuthOptionsResult | null> {
  const normalized = emailHint ? normalizeEmail(emailHint) : "";
  const body: Record<string, string> = {};
  if (normalized) {
    body.email = normalized;
  }
  const response = await fetch(`${AUTH_BASE}/passkeys/authentication/options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  await requireOk(response);
  return readData<PasskeyAuthOptionsResult>(response);
}

/**
 * Verify WebAuthn authentication credential response with challengeId and establish a session cookie.
 * Creates the same session/cookies as current primary login.
 */
export async function verifyPasskeyAuthentication(
  challengeId: string,
  authResponse: AuthenticationResponseJSON
): Promise<SessionData | null> {
  const response = await fetch(`${AUTH_BASE}/passkeys/authentication/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({
      challengeId,
      response: authResponse,
    }),
  });

  await requireOk(response);
  return readData<SessionData>(response);
}

/** Fetch all registered passkeys for the current user. Unwraps { passkeys: PasskeyItem[] }. */
export async function fetchPasskeys(): Promise<PasskeyItem[]> {
  const response = await fetch(`${AUTH_BASE}/passkeys`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
  });

  await requireOk(response);
  const data = await readData<{ passkeys?: PasskeyItem[] }>(response);
  return data?.passkeys ?? [];
}

/** Request WebAuthn registration creation options for registering a new passkey. Returns { challengeId, options }. */
export async function fetchPasskeyRegistrationOptions(
  name?: string
): Promise<PasskeyRegistrationOptionsResult | null> {
  const body: Record<string, string> = {};
  if (name?.trim()) {
    body.name = name.trim();
  }
  const response = await fetch(`${AUTH_BASE}/passkeys/registration/options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  await requireOk(response);
  return readData<PasskeyRegistrationOptionsResult>(response);
}

/** Verify a newly registered passkey credential response with challengeId. Unwraps { passkey: PasskeyItem }. */
export async function verifyPasskeyRegistration(
  challengeId: string,
  registrationResponse: RegistrationResponseJSON
): Promise<PasskeyItem | null> {
  const response = await fetch(`${AUTH_BASE}/passkeys/registration/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({
      challengeId,
      response: registrationResponse,
    }),
  });

  await requireOk(response);
  const data = await readData<{ passkey?: PasskeyItem }>(response);
  return data?.passkey ?? null;
}

/** Update the friendly name of an existing passkey. Unwraps { passkey: PasskeyItem }. */
export async function updatePasskey(passkeyId: string, name: string): Promise<PasskeyItem | null> {
  const response = await fetch(`${AUTH_BASE}/passkeys/${encodeURIComponent(passkeyId)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({ name: name.trim() }),
  });

  await requireOk(response);
  const data = await readData<{ passkey?: PasskeyItem }>(response);
  return data?.passkey ?? null;
}

/** Delete an existing passkey by ID. Unwraps { passkeyId, deleted }. */
export async function deletePasskey(passkeyId: string): Promise<DeletePasskeyResult | null> {
  const response = await fetch(`${AUTH_BASE}/passkeys/${encodeURIComponent(passkeyId)}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
  });

  await requireOk(response);
  return readData<DeletePasskeyResult>(response);
}
/* ─── Subscription API ───────────────────────────────────────────────────── */

/**
 * Fetch the current user's subscription overview (entitlements + subscription details).
 * Returns null on 401 (not logged in).
 */
export async function fetchCurrentSubscription(): Promise<SubscriptionOverview | null> {
  const response = await fetch(`${SUBSCRIPTIONS_BASE}/current`, {
    method: "GET",
    headers: { Accept: "application/json", ...CLIENT_TYPE_HEADERS },
    credentials: "include",
  });

  if (response.status === 401) return null;
  await requireOk(response);
  return readData<SubscriptionOverview>(response);
}

/* ─── Books / Greek Editor API ───────────────────────────────────────────── */

/** Retrieves the catalog of available Greek-editor rule definitions. */
export async function getRuleCatalog(token?: string): Promise<RuleCatalog> {
  /*
   * The rule catalog is edited from the backend, so the add-in must ask the
   * local/prod API for a fresh copy whenever a token is selected. The timestamp
   * only applies to this catalog endpoint and avoids stale Office WebView caches.
   */
  const cacheBuster = encodeURIComponent(String(Date.now()));
  const response = await fetch(`${BOOKS_BASE}/greek-editor/rules?_=${cacheBuster}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache, no-store",
      Pragma: "no-cache",
      ...buildServiceHeaders(token),
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
  });

  await requireOk(response);
  const result = (await response.json()) as ApiEnvelope<RuleCatalog>;
  return {
    rules: result?.data?.rules || [],
    preferences: result?.data?.preferences || {},
    dependencies: result?.data?.dependencies || {},
    sections: result?.data?.sections || {},
    karl: result?.data?.karl || null,
  };
}

export async function getRules(token?: string): Promise<Rule[]> {
  const catalog = await getRuleCatalog(token);
  return catalog.rules;
}

/* ─── Karl ──────────────────────────────────────────────────────────────── */

/*
 * Karl consent and assists are per account, so these calls never attach
 * `x-service-token`: the HttpOnly `sw_session` cookie is the only identity the
 * API can record the acceptance against. A caller without an account session is
 * refused with KARL_CONSENT_REQUIRED instead of silently using an anonymous
 * pool.
 */
async function fetchKarlJson<T>(path: string, init: RequestInit = {}): Promise<T | null> {
  const response = await fetch(`${BOOKS_BASE}/greek-editor${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
  });

  await requireOk(response);
  return readData<T>(response);
}

/** Remaining assists in the per-account pool shared by every channel. */
export async function fetchKarlAssists(): Promise<KarlAssists | null> {
  const data = await fetchKarlJson<{ assists?: KarlAssists }>("/karl-assists");
  return data?.assists ?? null;
}

/** Ask Karl about one Confusion finding. */
export async function fetchKarlVerdict(finding: KarlVerdictInput): Promise<KarlVerdictResult> {
  const data = await fetchKarlJson<{ verdict?: KarlVerdict; assists?: KarlAssists | null }>(
    "/karl-verdict",
    { method: "POST", body: JSON.stringify(finding) }
  );
  if (!data?.verdict) {
    throw new Error("Karl returned no verdict");
  }
  return { verdict: data.verdict, assists: data.assists ?? null };
}

/** Price a rule-wide or all-findings request before it runs. */
export async function estimateKarlVerdicts(
  findings: KarlVerdictInput[]
): Promise<KarlVerdictBatchEstimate> {
  const data = await fetchKarlJson<{ estimate?: KarlVerdictBatchEstimate }>(
    "/karl-verdicts/estimate",
    { method: "POST", body: JSON.stringify({ findings }) }
  );
  if (!data?.estimate) {
    throw new Error("Karl returned no estimate");
  }
  return data.estimate;
}

/**
 * Answer a batch in reading order. `limit` caps the assists the user approved
 * after an estimate; omitted, the API answers as many as the pool allows.
 */
export async function answerKarlVerdicts(
  findings: KarlVerdictInput[],
  limit?: number
): Promise<KarlVerdictBatchResult> {
  const body = limit === undefined ? { findings } : { findings, limit };
  const data = await fetchKarlJson<{
    results?: KarlVerdictBatchResult["results"];
    summary?: KarlVerdictBatchResult["summary"];
    assists?: KarlAssists | null;
  }>("/karl-verdicts", { method: "POST", body: JSON.stringify(body) });
  if (!data?.results || !data.summary) {
    throw new Error("Karl returned no batch results");
  }
  return { results: data.results, summary: data.summary, assists: data.assists ?? null };
}

/** Record the one-time Karl consent for the signed-in account. */
export async function acceptKarlConsent(): Promise<void> {
  await fetchKarlJson<{ consent?: unknown }>("/karl-consent", { method: "POST" });
}

/**
 * Record final decisions without text. Best effort at the call site: a failed
 * record must never undo the reader's decision.
 */
export async function recordKarlDecisions(decisions: KarlDecisionInput[]): Promise<void> {
  if (decisions.length === 0) return;
  await fetchKarlJson<{ recorded?: unknown }>("/karl-decisions", {
    method: "POST",
    body: JSON.stringify({ decisions }),
  });
}

/**
 * Upload a full DOCX file (as base64), apply corrections, return the
 * corrected DOCX as base64.
 */
export async function applyGreekEditorToDocx(
  token: string | undefined,
  base64Docx: string,
  ruleIds: string[],
  docxReviewMode: DocxReviewMode = "tracked",
  preferences: PreferenceValues = {}
): Promise<string> {
  const formData = new FormData();
  formData.append("files", base64ToBlob(base64Docx), "manuscript.docx");
  formData.append("editorOptions", JSON.stringify({ ruleIds, docxReviewMode, preferences }));

  const response = await fetch(`${BOOKS_BASE}/greek-editor/apply`, {
    method: "POST",
    headers: { ...buildServiceHeaders(token), ...CLIENT_TYPE_HEADERS },
    credentials: "include",
    body: formData,
  });

  await requireOk(response);
  return arrayBufferToBase64(await response.arrayBuffer());
}

/**
 * Apply corrections to a plain text string and return the corrected text
 * along with a change summary and optional report.
 */
export async function applyGreekEditorToText(
  token: string | undefined,
  inputText: string,
  ruleIds: string[],
  preferences: PreferenceValues = {}
): Promise<ApplyTextResult | null> {
  const response = await fetch(`${BOOKS_BASE}/greek-editor/apply-text`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildServiceHeaders(token),
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({
      inputText,
      editorOptions: { ruleIds, preferences, includeReport: true },
    }),
  });

  await requireOk(response);
  return readData<ApplyTextResult>(response);
}

/**
 * Scan text for Greek confusion-case occurrences without modifying anything.
 * Returns an array of occurrence objects (word, replacement, ruleId, offsets).
 * The optional legacy raw token is forwarded like every other books call; Karl
 * calls stay account-only.
 */
export async function scanGreekEditorConfusion(
  token: string | undefined,
  inputText: string,
  ruleIds: string[]
): Promise<Occurrence[]> {
  const response = await fetch(`${BOOKS_BASE}/greek-editor/scan-confusion`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildServiceHeaders(token),
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({ inputText, editorOptions: { ruleIds } }),
  });

  await requireOk(response);
  const result = (await response.json()) as ApiEnvelope<{ occurrences?: Occurrence[] }>;
  return result?.data?.occurrences || [];
}

/**
 * Scan a full DOCX file (as base64) for suggested editorial corrections.
 * Returns { changes, summary, wordCount } without modifying the document.
 */
export async function scanSuggestions(
  token: string | undefined,
  base64Docx: string,
  ruleIds: string[],
  preferences: PreferenceValues = {}
): Promise<ScanSuggestionsResult | null> {
  const formData = new FormData();
  formData.append("files", base64ToBlob(base64Docx), "manuscript.docx");
  formData.append("editorOptions", JSON.stringify({ ruleIds, preferences }));

  const response = await fetch(`${BOOKS_BASE}/greek-editor/scan-suggestions`, {
    method: "POST",
    headers: { ...buildServiceHeaders(token), ...CLIENT_TYPE_HEADERS },
    credentials: "include",
    body: formData,
  });

  await requireOk(response);
  return readData<ScanSuggestionsResult>(response);
}

/**
 * Scan active paragraph text for live suggestions without modifying anything.
 * Returns { changes, summary, wordCount } for task-pane review.
 *
 * In-flight calls with the same text + rule set coalesce into one network
 * call so a live-polling re-trigger never duplicates the request.
 */
export async function scanTextSuggestions(
  token: string | undefined,
  inputText: string,
  ruleIds: string[],
  preferences: PreferenceValues = {}
): Promise<ScanTextSuggestionsResult | null> {
  const key = `scanTextSuggestions:${token || ""}:${stableKey({
    text: inputText,
    ruleIds: [...ruleIds].sort(),
    preferences,
  })}`;
  return coalesceInFlight(key, async () => {
    const response = await fetch(`${BOOKS_BASE}/greek-editor/scan-text-suggestions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildServiceHeaders(token),
        ...CLIENT_TYPE_HEADERS,
      },
      credentials: "include",
      body: JSON.stringify({ inputText, editorOptions: { ruleIds, preferences } }),
    });

    await requireOk(response);
    return readData<ScanTextSuggestionsResult>(response);
  });
}

/**
 * Scan plain text for word repetitions (lexical density engine) without
 * modifying anything. Options accept { windowSize, minOccurrences,
 * customStopwords } — the same contract used by the web portal. Returns
 * { repetitions, statistics }.
 *
 * Note: the backend repetition controller reads `req.body.options`, so the
 * body key here is `options` (NOT `editorOptions` like the rule endpoints).
 *
 * In-flight calls with the same text + options coalesce into one network
 * call; different options run as separate requests.
 */
export async function scanRepetitions(
  token: string | undefined,
  inputText: string,
  options: RepetitionOptions = {}
): Promise<ScanRepetitionsResult | null> {
  const key = `scanRepetitions:${token || ""}:${stableKey({
    text: inputText,
    options,
  })}`;
  return coalesceInFlight(key, async () => {
    const response = await fetch(`${BOOKS_BASE}/greek-editor/scan-repetitions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildServiceHeaders(token),
        ...CLIENT_TYPE_HEADERS,
      },
      credentials: "include",
      body: JSON.stringify({ inputText, options }),
    });

    await requireOk(response);
    return readData<ScanRepetitionsResult>(response);
  });
}

export interface AiSuggestionsResponse {
  suggestions: string[];
  remainingQuota: number | null;
}

export async function requestAiSuggestions(
  token: string | undefined,
  word: string,
  forms: string[],
  context: string
): Promise<AiSuggestionsResponse | null> {
  const response = await fetch(`${BOOKS_BASE}/greek-editor/suggest-replacements`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildServiceHeaders(token),
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify({ word, forms, context }),
  });

  await requireOk(response);
  return readData<AiSuggestionsResponse>(response);
}

export async function submitFalsePositiveReport(payload: unknown): Promise<unknown> {
  const response = await fetch("/api/false-positive-reports", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  await requireOk(response);
  return readData<unknown>(response);
}

export interface CustomRulePreset {
  presetId: string;
  alias: string;
  description?: string;
  ruleIds: string[];
  preferences?: PreferenceValues;
  managed?: boolean;
  source?: "user" | "admin";
}

export async function fetchCustomRulePresets(): Promise<CustomRulePreset[]> {
  const response = await fetch("/api/account/books/greek-editor/rule-presets", {
    method: "GET",
    headers: { Accept: "application/json", ...CLIENT_TYPE_HEADERS },
    credentials: "include",
  });

  if (response.status === 401) return [];
  await requireOk(response);
  const result = (await response.json()) as ApiEnvelope<{ presets?: CustomRulePreset[] }>;
  return result?.data?.presets || [];
}

export async function createGreekEditorRulePreset(payload: {
  alias: string;
  description?: string;
  ruleIds: string[];
  preferences?: PreferenceValues;
}): Promise<CustomRulePreset | null> {
  const response = await fetch("/api/account/books/greek-editor/rule-presets", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (response.status === 401) return null;
  await requireOk(response);
  const result = (await response.json()) as ApiEnvelope<{ preset: CustomRulePreset }>;
  return result?.data?.preset || null;
}

export const splitManagedPresets = (all: CustomRulePreset[]) => ({
  managed: all.filter((p) => p.managed),
  personal: all.filter((p) => !p.managed),
});

export const buildForkPayload = (preset: CustomRulePreset) => ({
  alias: `${preset.alias} (copy)`,
  description: preset.description,
  ruleIds: [...preset.ruleIds],
  preferences: { ...(preset.preferences || {}) },
});

export async function fetchRecentRuleSets(): Promise<RecentRuleSet[]> {
  const response = await fetch("/api/account/books/greek-editor/recent-rule-sets", {
    method: "GET",
    headers: { Accept: "application/json", ...CLIENT_TYPE_HEADERS },
    credentials: "include",
  });
  if (response.status === 401) return [];
  await requireOk(response);
  const result = (await response.json()) as ApiEnvelope<{ recentRuleSets?: RecentRuleSet[] }>;
  return result?.data?.recentRuleSets || [];
}

export async function recordRecentRuleSet(payload: {
  ruleIds: string[];
  preferences?: PreferenceValues;
  protectedTerms?: string[];
  outputDefaults?: { docxReviewMode?: "direct" | "tracked"; includeReport?: boolean };
}): Promise<RecentRuleSet[]> {
  const response = await fetch("/api/account/books/greek-editor/recent-rule-sets", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...CLIENT_TYPE_HEADERS,
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (response.status === 401) return [];
  await requireOk(response);
  const result = (await response.json()) as ApiEnvelope<{ recentRuleSets?: RecentRuleSet[] }>;
  return result?.data?.recentRuleSets || [];
}
